/*eslint max-lines-per-function: ["error", 275]*/
/*eslint complexity: ["error", 31]*/
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Tab } from 'react-bootstrap';

import { TabsHistory } from '../../../app/components/TabsHistory';
import { SalesInvoiceTab } from '../../components/SalesInvoices/SalesInvoiceTab';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { SalesInvoiceBasicData } from '../../components/SalesInvoices/SalesInvoiceBasicData';
import {
  KSFStatusEnum,
  SalesInvoiceStatusEnum,
} from '../../../common/pokCore/validation/enums';
import {
  SalesInvoicePositions,
  TabLocationState,
} from '../../components/SalesInvoices/SalesInvoicePositions';
import { SalesInvoiceMediaPlanPositions } from '../../components/SalesInvoices/SalesInvoiceMediaplanPositions';
import { SalesInvoiceSummary } from '../../components/SalesInvoices/SalesInvoiceSummary';
import useSalesInvoiceData from '../../components/SalesInvoices/useSalesInvoiceData';
import { BadCompany } from '../../../common/components/NotAuthorized/BadCompany';
import {
  GetSalesInvoiceDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNavigation } from '../../../common/navigation';
import { useLocation } from '../../../common/hooks/useLocation';
import { Waiting } from '../../../common/components/Waiting/Waiting';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { SalesInvoiceHeader } from '../../components/SalesInvoices/SalesInvoiceHeader';

import { SalesInvoiceEditorTabs } from './SalesInvoiceEditorTabs';
import useIsAuthorized from './useIsAuthorized';

interface TabProps {
  key: SalesInvoiceEditorTabs;
  title: string;
  component: React.ReactNode;
  disabled: boolean;
  notSaved?: boolean;
}

const getTabClassName = (notSaved?: boolean, hasProjects?: boolean) =>
  notSaved && hasProjects ? 'not-saved-tab' : '';

const isBasicDataNotSaved = (
  notSavedTab: SalesInvoiceEditorTabs | undefined,
  salesInvoice?: GetSalesInvoiceDto,
) =>
  notSavedTab === SalesInvoiceEditorTabs.BASIC_DATA ||
  !(salesInvoice && salesInvoice.id);

const checkIsReadOnly = (salesInvoice?: GetSalesInvoiceDto) =>
  [
    SalesInvoiceStatusEnum.Generated,
    SalesInvoiceStatusEnum.Sent,
    SalesInvoiceStatusEnum.Paid,
    SalesInvoiceStatusEnum.InKSeF,
  ].includes(salesInvoice?.status as SalesInvoiceStatusEnum);

export const SalesInvoiceEditorView: React.FC = () => {
  const {
    projects,
    setProjects,
    error,
    convertedSalesInvoice,
    handleRefresh,
    handleRemove,
    salesInvoice,
    attachmentIds,
    isReady,
    isLoading,
    refresh,
    parentAmount,
    test,
    orderNumbers,
  } = useSalesInvoiceData();
  const location = useLocation<TabLocationState>();
  const nav = useNavigation();
  const { t, tk } = useTranslation('fvsLng');
  const authorized = useIsAuthorized(projects);
  const pok = usePokCore();
  const notifications = useNotifications();
  const [notSavedTab, setNotSavedTab] = useState<
    SalesInvoiceEditorTabs | undefined
  >(location?.state?.notSavedTab as SalesInvoiceEditorTabs);
  const [summaryRefresh, setSummaryRefresh] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(
    SalesInvoiceEditorTabs.BASIC_DATA,
  );

  useEffect(() => {
    if (activeTab === SalesInvoiceEditorTabs.SUMMARY) {
      setSummaryRefresh(r => !r);
    }
  }, [activeTab, refresh]);

  const isInActive = salesInvoice && !salesInvoice.active;
  const isRejectedFromKSeF =
    salesInvoice?.statusKsef === KSFStatusEnum.RejectedInKSeF;

  const isReadOnly = checkIsReadOnly(salesInvoice) || isInActive;
  const initialProjectIds = salesInvoice?.salesInvoiceProjects?.map(
    ({ project }) => project.id,
  );

  const handlePrintInvoice = async () => {
    if (!salesInvoice?.id) {
      return;
    }
    try {
      await pok.pdfPrinters.openInvoice(salesInvoice.id, false, false);
    } catch (error) {
      notifications.caughtError(error as ResponseError);
    }
  };

  const handlePreviewInvoice = async () => {
    if (!salesInvoice?.id) {
      return;
    }
    try {
      await pok.pdfPrinters.previewInvoice(salesInvoice.id);
    } catch (error) {
      notifications.caughtError(error as ResponseError);
    }
  };

  const tabs: TabProps[] = useMemo(() => {
    const commonProps = {
      convertedSalesInvoice,
      salesInvoice,
      projects,
      setProjects,
      id: salesInvoice?.id,
      handleRefresh,
      handleRemove,
      setNotSavedTab,
      refresh,
      purchaser: salesInvoice?.purchaser || projects?.[0]?.purchaser,
      recipientClient: salesInvoice?.recipientClient,
      positions: salesInvoice?.salesInvoicePositions,
      company: salesInvoice?.company,
      number: salesInvoice?.number,
      allowCorrection: salesInvoice?.allowCorrection,
      isDeactivated: isInActive,
    };

    return [
      {
        key: SalesInvoiceEditorTabs.BASIC_DATA,
        title: t(tk.finance.basicData),
        component: (
          <SalesInvoiceBasicData
            {...commonProps}
            initialProjectIds={initialProjectIds}
            test={test}
            readOnly={
              isReadOnly ||
              (!!notSavedTab &&
                notSavedTab !== SalesInvoiceEditorTabs.BASIC_DATA)
            }
          />
        ),
        disabled: false,
        notSaved: isBasicDataNotSaved(notSavedTab, salesInvoice),
      },
      {
        key: SalesInvoiceEditorTabs.MEDIAPLAN_POSITIONS,
        title: t(tk.prLang[SalesInvoiceEditorTabs.MEDIAPLAN_POSITIONS]),
        component: (
          <SalesInvoiceMediaPlanPositions
            {...commonProps}
            salesInvoice={salesInvoice}
            readOnly={
              isReadOnly ||
              (!!notSavedTab &&
                notSavedTab !== SalesInvoiceEditorTabs.MEDIAPLAN_POSITIONS)
            }
            parentAmount={parentAmount}
          />
        ),
        disabled: !salesInvoice,
        notSaved: notSavedTab === SalesInvoiceEditorTabs.MEDIAPLAN_POSITIONS,
      },
      {
        key: SalesInvoiceEditorTabs.INVOICE_POSITIONS,
        title: t(tk.finance.invoicePositions),
        component: (
          <SalesInvoicePositions
            {...commonProps}
            salesInvoice={salesInvoice}
            readOnly={
              isReadOnly ||
              (!!notSavedTab &&
                notSavedTab !== SalesInvoiceEditorTabs.INVOICE_POSITIONS)
            }
            parentAmount={parentAmount}
          />
        ),
        disabled: !salesInvoice?.invoiceEstimateItems?.length,
        notSaved: notSavedTab === SalesInvoiceEditorTabs.INVOICE_POSITIONS,
      },
      {
        key: SalesInvoiceEditorTabs.SUMMARY,
        title: t(tk.finance.summary),
        component: (
          <SalesInvoiceSummary
            {...commonProps}
            readOnly={
              isReadOnly ||
              (!!notSavedTab && notSavedTab !== SalesInvoiceEditorTabs.SUMMARY)
            }
            initialOrderNumbers={orderNumbers}
            refresh={summaryRefresh}
          />
        ),
        disabled: !salesInvoice?.salesInvoicePositions?.length,
        notSaved: notSavedTab === SalesInvoiceEditorTabs.SUMMARY,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesInvoice, convertedSalesInvoice, isReady, orderNumbers]);

  if (!authorized) {
    return <BadCompany companyId={salesInvoice?.company?.id} />;
  }

  if (isLoading) {
    return <Waiting />;
  }

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title>
          <SalesInvoiceHeader
            isInActive={isInActive}
            salesInvoice={salesInvoice}
            projects={projects}
            attachmentIds={attachmentIds}
            goToProject={id => nav.projectDetails(id, true, 'attachments')}
            goToSalesInvoice={id =>
              nav.salesInvoiceEditor({ salesInvoiceId: id, newWindow: true })
            }
            onPrintInvoice={handlePrintInvoice}
            onPreviewInvoice={handlePreviewInvoice}
          />
        </Card.Title>
        {isRejectedFromKSeF && (
          <Alert variant="danger" className="mt-3">
            <div>{t(tk.finance.rejectedFromKSeFEditWarning)}</div>
            <div className="mt-1">
              {t(tk.finance.rejectedFromKSeFEditSteps)}
            </div>
          </Alert>
        )}
        <TabsHistory
          activeKey={SalesInvoiceEditorTabs.BASIC_DATA}
          id="product"
          className="my-3"
          setActiveTab={setActiveTab}
        >
          {isReady &&
            tabs.map(({ key, title, component, notSaved, disabled }) => (
              <Tab
                key={key}
                eventKey={key}
                tabClassName={getTabClassName(notSaved, projects.length > 0)}
                title={`${title} ${notSaved && projects.length ? `(${t(tk.finance.unwrittenUppercase)})` : ''}`}
                className="m-3"
                disabled={disabled}
              >
                <SalesInvoiceTab
                  isLoading={!projects.length}
                  component={component}
                  error={error}
                />
              </Tab>
            ))}
        </TabsHistory>
      </Card.Body>
    </Card>
  );
};
